import type * as z from 'zod/v4';

/**
 * Create a validation function for a Zod Object Schema.
 * The function is used to create a public validation function that is exposed from the package.
 *
 * @param Schema Any Zod Object Schema to run validation on
 * @returns Returns a parse function that will run validation on the input value
 */
export function validate<TSchema extends z.ZodObject>(Schema: TSchema) {
  return (
    ...validators: (keyof z.infer<typeof Schema>)[]
  ): {
    readonly parse: (value: unknown) => z.infer<typeof Schema> | undefined;
  } => {
    const picks = validators.reduce((vd, v) => ({ ...vd, [v]: true }), {});
    const validateSchema = Schema.pick(picks).partial();

    const parse = (value: unknown): z.infer<typeof Schema> | undefined => {
      try {
        return validateSchema.parse(value) as z.infer<TSchema>;
      } catch {
        return undefined;
      }
    };

    return { parse } as const;
  };
}
